import React from 'react';
import moment from 'moment';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { grey } from '@mui/material/colors';
import { DateFormats } from 'constants/common';
import { useAccountContact } from 'modules/account';
import { customersApi } from 'modules/customers/api';
import { MUITable } from 'components/shared/MUITable';
import { Delete, Edit, Phone } from '@mui/icons-material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { CustomerDetails } from '@trnsact/trnsact-shared-types';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { ContactAddressTooltip } from './ContactAddressTooltip';

interface AddressInfo {
  id: string;
  city: string | null;
  address: string | null;
  country: string | null;
  postalCode: string | null;
  stateProvince: string | null;
}

interface CustomerContactItem {
  id: string;
  title: string;
  phone: string;
  lastName: string;
  firstName: string;
  lastContactDate: string;
  contactType: 'OWNER_PG' | 'ACCOUNT_CONTACT';
  accountContactDetails: {
    id: string;
    email: string;
    contactAddress: AddressInfo | null;
    deliveryAddress: AddressInfo | null;
  };
  ownerPgDetails: {
    ownerPgId: string;
    creditApplicationId: string;
  };
}

interface Props {
  customerAccountId: string;
  customerDetails: CustomerDetails;
  customerContacts: CustomerContactItem[];
}

export const CompanyInformationCard = ({ customerAccountId, customerDetails, customerContacts }: Props) => {
  const { handleOpen } = useModal(ModalsKeys.AccountContactDialog);
  const { handleOpen: handleOpenConfirmationModal } = useModal(ModalsKeys.Confirmation);
  const { handleDeleteAccountContact, isDeleting } = useAccountContact();

  const tableData = (customerContacts ?? []).map(contact => ({
    ...contact,
    id: contact.accountContactDetails?.id || contact.ownerPgDetails?.ownerPgId || '',
  }));

  return (
    <CardContainer>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" component="p">
            {customerDetails.name}
          </Typography>

          <Box>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                handleOpen({ contactId: '', accountId: customerAccountId, customerInfo: customerDetails });
              }}
            >
              Add contact
            </Button>
          </Box>
        </Stack>

        <Stack direction="row" gap={1}>
          {customerDetails.phone && (
            <Stack direction="row" alignItems="center" sx={{ color: grey[600] }}>
              <Phone />
              <Typography variant="body1">{customerDetails.phone}</Typography>
            </Stack>
          )}

          {customerDetails.address && (
            <Stack direction="row" alignItems="center" sx={{ color: grey[600] }}>
              <LocationOnIcon />
              <Typography variant="body1">{customerDetails.address}</Typography>
            </Stack>
          )}
        </Stack>

        <MUITable<any>
          isLoading={false}
          tableData={tableData}
          columns={[
            {
              header: 'Contact',
              renderKey: 'id',
              renderCell: (contact: CustomerContactItem) =>
                [contact.firstName, contact.lastName].filter(Boolean).join(' ') || '-',
            },
            {
              header: 'Email',
              renderKey: 'email',
              renderCell: (contact: CustomerContactItem) => contact.accountContactDetails?.email ?? '-',
            },
            {
              header: 'Title',
              renderKey: 'title',
              renderCell: (contact: CustomerContactItem) => contact.title ?? '-',
            },
            {
              header: 'Phone',
              renderKey: 'phone',
              renderCell: (contact: CustomerContactItem) => contact.phone ?? '-',
            },
            {
              header: 'Last Contact',
              renderKey: 'id',
              renderCell: (contact: CustomerContactItem) =>
                contact.lastContactDate ? moment(contact.lastContactDate).format(DateFormats.Short) : '-',
            },
            {
              header: 'Actions',
              renderKey: 'id',
              renderCell: (contact: CustomerContactItem) => (
                <RenderWithCondition condition={contact.contactType === 'ACCOUNT_CONTACT'}>
                  <Stack direction="row" gap="0.5rem">
                    <ContactAddressTooltip
                      contactAddress={contact.accountContactDetails?.contactAddress}
                      deliveryAddress={contact.accountContactDetails?.deliveryAddress}
                    />

                    <IconButton
                      size="small"
                      className="icon-btn-bordered"
                      disabled={!contact.accountContactDetails?.id}
                      onClick={() => {
                        handleOpen({
                          accountId: customerAccountId,
                          customerInfo: customerDetails,
                          contactId: contact.accountContactDetails?.id,
                        });
                      }}
                    >
                      <Edit color="primary" />
                    </IconButton>

                    <IconButton
                      size="small"
                      color="error"
                      loading={isDeleting}
                      className="icon-btn-bordered"
                      disabled={!contact.accountContactDetails?.id}
                      onClick={() => {
                        handleOpenConfirmationModal({
                          title: 'Confirmation',
                          message: 'Are you sure you want to delete this contact?',
                          onConfirm: async () => {
                            await handleDeleteAccountContact(
                              {
                                id: contact.accountContactDetails?.id,
                              },
                              () => ({
                                awaitRefetchQueries: true,
                                refetchQueries: [customersApi.getCustomerContacts],
                              })
                            );
                          },
                        });
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </Stack>
                </RenderWithCondition>
              ),
            },
          ]}
        />
      </Stack>
    </CardContainer>
  );
};
